import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
  TablePagination,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Switch,
  FormLabel,
  Tooltip,
  Stack,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add,
  Visibility,
  Search,
  Refresh,
  AttachMoney,
  Phone,
  WhatsApp,
  Email,
  AccountBalance,
  MoreVert,
  Notifications,
  Savings,
  Clear,
  Edit,
  Assessment,
  MenuBook,
  CheckCircle,
  Warning,
  Block,
  Star,
} from '@mui/icons-material';
import { BorrowerAPIItem } from '../../models/LendingModels';
import { formatCurrency } from '../../utils/CurrencyFormat';
import { getBorrowers, createBorrowers, sendBalanceNotification } from '../../services/request/Borrowers';
import { createDeposit } from '../../services/request/Deposits';
import { getAllPayments } from '../../services/request/Payments';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DebitCaptureModal from '../admin/outstandings/DebitCaptureChild';
import BrochureViewer from '../admin/brochure/BrochureViewer';
import { PageHeader, MetricStrip, SectionPanel, EmptyState, DataToolbar } from '../../shared-components/ui';

type QuickFilterType = 'all' | 'outstanding' | 'deposits' | 'zero_balance' | 'active' | 'blacklisted';
type SortOptionType =
  | 'outstanding_desc'
  | 'outstanding_asc'
  | 'name_asc'
  | 'name_desc'
  | 'deposit_desc'
  | 'recent_payment'
  | 'credit_score';

const AVATAR_COLORS = [
  { bg: '#0F766E', text: '#FFFFFF' }, // Teal
  { bg: '#1D4ED8', text: '#FFFFFF' }, // Blue
  { bg: '#7C3AED', text: '#FFFFFF' }, // Purple
  { bg: '#C2410C', text: '#FFFFFF' }, // Orange
  { bg: '#B91C1C', text: '#FFFFFF' }, // Rose
  { bg: '#047857', text: '#FFFFFF' }, // Emerald
  { bg: '#475569', text: '#FFFFFF' }, // Slate
];

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

function formatRelativeDays(dateStr?: string): { text: string; isRecent: boolean } {
  if (!dateStr) return { text: 'No payments', isRecent: false };
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return { text: 'No payments', isRecent: false };
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return { text: 'Today', isRecent: true };
    if (diffDays === 1) return { text: 'Yesterday', isRecent: true };
    if (diffDays < 30) return { text: `${diffDays}d ago`, isRecent: diffDays <= 7 };
    if (diffDays < 365) return { text: `${Math.floor(diffDays / 30)}mo ago`, isRecent: false };
    return { text: `${Math.floor(diffDays / 365)}y ago`, isRecent: false };
  } catch {
    return { text: 'No payments', isRecent: false };
  }
}

const CustomerManagement: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Data states
  const [borrowers, setBorrowers] = useState<BorrowerAPIItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mainTabValue, setMainTabValue] = useState(0);

  // Search & Filter & Sort states
  const [searchTerm, setSearchTerm] = useState('');
  const [quickFilter, setQuickFilter] = useState<QuickFilterType>('all');
  const [sortBy, setSortBy] = useState<SortOptionType>('outstanding_desc');
  const [customerPage, setCustomerPage] = useState(0);
  const [customerRowsPerPage, setCustomerRowsPerPage] = useState(10);

  // Global Payments tab states
  const [allPayments, setAllPayments] = useState<any[]>([]);
  const [loadingAllPayments, setLoadingAllPayments] = useState(false);
  const [paymentSearch, setPaymentSearch] = useState('');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('ALL');
  const [paymentPage, setPaymentPage] = useState(0);
  const [paymentRowsPerPage, setPaymentRowsPerPage] = useState(10);
  const [totalPayments, setTotalPayments] = useState(0);

  // Modal states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openCaptureDebitDialog, setOpenCaptureDebitDialog] = useState(false);
  const [selectedBorrowerId, setSelectedBorrowerId] = useState<string | null>(null);

  // More options menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBorrowerForMenu, setSelectedBorrowerForMenu] = useState<BorrowerAPIItem | null>(null);

  // Deposit modal
  const [openDepositDialog, setOpenDepositDialog] = useState(false);
  const [depositBorrower, setDepositBorrower] = useState<BorrowerAPIItem | null>(null);
  const [depositForm, setDepositForm] = useState({
    amount: '',
    depositDate: new Date().toISOString().slice(0, 10),
    maturityDate: '',
    referenceNumber: '',
    source: 'BANK_TRANSFER',
    notes: '',
    notifyCustomer: true,
  });
  const [savingDeposit, setSavingDeposit] = useState(false);

  // Notification modal
  const [openNotifyDialog, setOpenNotifyDialog] = useState(false);
  const [notificationData, setNotificationData] = useState({
    useSystemContact: true,
    email: '',
    contactNumber: '',
    sendEmail: true,
    sendSMS: false,
  });

  // Welcome brochure
  const [openBrochureDialog, setOpenBrochureDialog] = useState(false);
  const [selectedBorrowerForBrochure, setSelectedBorrowerForBrochure] = useState<BorrowerAPIItem | null>(null);

  // Add borrower form state
  const [newBorrower, setNewBorrower] = useState({
    name: '',
    comments: '',
    contactNumber: '',
    whatsappNumber: '',
    isContactSameAsWhatsapp: false,
    email: '',
    address: '',
    pincode: '',
    city: '',
    state: '',
    referralCode: '',
    creditScore: '',
  });

  // Fetch full borrowers list
  const fetchBorrowers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch high limit to allow client-side instant search, filtering & sorting
      const response = await getBorrowers(0, 2000, {});
      if (response && (response.code === 200 || response.status === 200 || response.result)) {
        const list = response.result || response.data || [];
        setBorrowers(list);
      } else {
        setError(`Failed to load customers: ${response?.message || 'Unknown response'}`);
      }
    } catch (err: any) {
      setError(`Failed to fetch customers: ${err?.message || 'Network error'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBorrowers();
  }, [fetchBorrowers]);

  // Fetch payments for Tab 1
  const fetchAllPayments = useCallback(async () => {
    try {
      setLoadingAllPayments(true);
      const filters: Record<string, any> = {};
      if (paymentTypeFilter !== 'ALL') {
        filters.paymentType = paymentTypeFilter;
      }
      const response = await getAllPayments(paymentPage, paymentRowsPerPage, filters);
      const list = response?.data?.result || response?.result || [];
      const total = response?.data?.totalCount || response?.totalCount || list.length;
      setAllPayments(list);
      setTotalPayments(total);
    } catch (err: any) {
      toast.error('Failed to load global payment history');
    } finally {
      setLoadingAllPayments(false);
    }
  }, [paymentPage, paymentRowsPerPage, paymentTypeFilter]);

  useEffect(() => {
    if (mainTabValue === 1) {
      fetchAllPayments();
    }
  }, [mainTabValue, fetchAllPayments]);

  // Reset pagination on search / filter changes
  useEffect(() => {
    setCustomerPage(0);
  }, [searchTerm, quickFilter, sortBy]);

  // Summary Metrics calculations
  const totalCustomersCount = borrowers.length;
  const borrowersWithDebt = useMemo(() => borrowers.filter((b) => (b.totalOutstanding || 0) > 0), [borrowers]);
  const borrowersWithDeposits = useMemo(() => borrowers.filter((b) => (b.totalDeposit || 0) > 0), [borrowers]);
  const totalOutstandingSum = useMemo(
    () => borrowers.reduce((acc, b) => acc + (b.totalOutstanding || 0), 0),
    [borrowers]
  );
  const totalDepositsSum = useMemo(
    () => borrowers.reduce((acc, b) => acc + (b.totalDeposit || 0), 0),
    [borrowers]
  );
  const goodCreditCount = useMemo(
    () => borrowers.filter((b) => b.creditStatus === 'GOOD' || (!b.blacklisted && b.creditStatus !== 'BLOCKED')).length,
    [borrowers]
  );
  const blacklistedCount = useMemo(
    () => borrowers.filter((b) => b.blacklisted === true || b.creditStatus === 'BLOCKED' || b.creditStatus === 'BAD').length,
    [borrowers]
  );
  const zeroBalanceCount = useMemo(
    () => borrowers.filter((b) => (b.totalOutstanding || 0) === 0),
    [borrowers]
  ).length;

  // Filtered & Sorted Customer List
  const filteredCustomers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let res = borrowers.filter((b) => {
      // Search matching across multiple fields
      if (q) {
        const matchesName = (b.name || '').toLowerCase().includes(q);
        const matchesId = (b.borrowerId || b.id || '').toLowerCase().includes(q);
        const matchesPhone = (b.contactNumber || '').includes(q) || (b.whatsappNumber || '').includes(q);
        const matchesEmail = (b.email || '').toLowerCase().includes(q);
        const matchesCity = (b.city || '').toLowerCase().includes(q) || (b.state || '').toLowerCase().includes(q);
        const matchesComments = (b.comments || '').toLowerCase().includes(q);
        const matchesRef = (b.referralCode || '').toLowerCase().includes(q);
        if (!matchesName && !matchesId && !matchesPhone && !matchesEmail && !matchesCity && !matchesComments && !matchesRef) {
          return false;
        }
      }

      // Quick filter
      if (quickFilter === 'outstanding') return (b.totalOutstanding || 0) > 0;
      if (quickFilter === 'deposits') return (b.totalDeposit || 0) > 0;
      if (quickFilter === 'zero_balance') return (b.totalOutstanding || 0) === 0;
      if (quickFilter === 'active') return b.creditStatus === 'GOOD' || (!b.blacklisted && b.creditStatus !== 'BLOCKED');
      if (quickFilter === 'blacklisted') return b.blacklisted === true || b.creditStatus === 'BLOCKED' || b.creditStatus === 'BAD';
      return true;
    });

    // Sorting
    res = [...res].sort((a, b) => {
      switch (sortBy) {
        case 'outstanding_desc':
          return (b.totalOutstanding || 0) - (a.totalOutstanding || 0);
        case 'outstanding_asc':
          return (a.totalOutstanding || 0) - (b.totalOutstanding || 0);
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'deposit_desc':
          return (b.totalDeposit || 0) - (a.totalDeposit || 0);
        case 'recent_payment':
          return new Date(b.lastPaymentDate || 0).getTime() - new Date(a.lastPaymentDate || 0).getTime();
        case 'credit_score':
          return (b.creditScore || 0) - (a.creditScore || 0);
        default:
          return (b.totalOutstanding || 0) - (a.totalOutstanding || 0);
      }
    });

    return res;
  }, [borrowers, searchTerm, quickFilter, sortBy]);

  // Paginated slice
  const paginatedCustomers = useMemo(() => {
    const start = customerPage * customerRowsPerPage;
    return filteredCustomers.slice(start, start + customerRowsPerPage);
  }, [filteredCustomers, customerPage, customerRowsPerPage]);

  // Filtered Payments for Tab 1
  const filteredPayments = useMemo(() => {
    if (!paymentSearch.trim()) return allPayments;
    const q = paymentSearch.toLowerCase();
    return allPayments.filter((p) => {
      const bName = (p.borrowerName || p.customerName || '').toLowerCase();
      const pNote = (p.note || '').toLowerCase();
      const dId = (p.debitId || '').toLowerCase();
      return bName.includes(q) || pNote.includes(q) || dId.includes(q);
    });
  }, [allPayments, paymentSearch]);

  // Navigation handlers
  const handleViewCustomer = (borrowerId: string) => {
    navigate(`/admin/customers/${borrowerId}`);
  };

  const handleViewDebits = (borrowerId: string) => {
    navigate(`/admin/customers/${borrowerId}/debits`);
  };

  const handleOpenCaptureDebitDialog = (borrowerId: string) => {
    setSelectedBorrowerId(borrowerId);
    setOpenCaptureDebitDialog(true);
  };

  const handleCloseCaptureDebitDialog = () => {
    setOpenCaptureDebitDialog(false);
    setSelectedBorrowerId(null);
  };

  const handleDebitCreated = () => {
    fetchBorrowers();
    toast.success('Debit captured successfully');
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, borrower: BorrowerAPIItem) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedBorrowerForMenu(borrower);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Deposit handlers
  const handleOpenDepositDialog = (borrower: BorrowerAPIItem) => {
    setDepositBorrower(borrower);
    setDepositForm({
      amount: '',
      depositDate: new Date().toISOString().slice(0, 10),
      maturityDate: '',
      referenceNumber: '',
      source: 'BANK_TRANSFER',
      notes: '',
      notifyCustomer: true,
    });
    setOpenDepositDialog(true);
    handleMenuClose();
  };

  const handleCloseDepositDialog = () => {
    setOpenDepositDialog(false);
    setDepositBorrower(null);
  };

  const handleSubmitDeposit = async () => {
    if (!depositBorrower) return;
    const amt = Number(depositForm.amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid deposit amount');
      return;
    }
    try {
      setSavingDeposit(true);
      await createDeposit(depositBorrower.id, {
        amount: amt,
        depositDate: depositForm.depositDate,
        maturityDate: depositForm.maturityDate || undefined,
        referenceNumber: depositForm.referenceNumber || undefined,
        source: depositForm.source || undefined,
        notes: depositForm.notes || undefined,
        notifyCustomer: depositForm.notifyCustomer,
      });
      toast.success('Deposit recorded successfully');
      handleCloseDepositDialog();
      fetchBorrowers();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to record deposit');
    } finally {
      setSavingDeposit(false);
    }
  };

  // Notification handlers
  const handleNotifyCustomer = () => {
    if (selectedBorrowerForMenu) {
      setNotificationData({
        useSystemContact: true,
        email: selectedBorrowerForMenu.email || '',
        contactNumber: selectedBorrowerForMenu.contactNumber || '',
        sendEmail: true,
        sendSMS: false,
      });
      setOpenNotifyDialog(true);
    }
    handleMenuClose();
  };

  const handleCloseNotifyDialog = () => {
    setOpenNotifyDialog(false);
    setNotificationData({
      useSystemContact: true,
      email: '',
      contactNumber: '',
      sendEmail: true,
      sendSMS: false,
    });
  };

  const handleSendNotification = async () => {
    if (!selectedBorrowerForMenu) return;
    if (!notificationData.sendEmail && !notificationData.sendSMS) {
      toast.error('Please select at least one notification channel (Email or SMS)');
      return;
    }
    try {
      setLoading(true);
      const payload: any = {
        useSystemContact: notificationData.useSystemContact,
        sendEmail: notificationData.sendEmail,
        sendSMS: notificationData.sendSMS,
      };
      if (!notificationData.useSystemContact) {
        if (notificationData.sendEmail) payload.email = notificationData.email;
        if (notificationData.sendSMS) payload.contactNumber = notificationData.contactNumber;
      }
      await sendBalanceNotification(selectedBorrowerForMenu.id, payload);
      toast.success('Balance notification sent successfully');
      handleCloseNotifyDialog();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  // Add Borrower handlers
  const handleOpenAddDialog = () => {
    setNewBorrower({
      name: '',
      comments: '',
      contactNumber: '',
      whatsappNumber: '',
      isContactSameAsWhatsapp: false,
      email: '',
      address: '',
      pincode: '',
      city: '',
      state: '',
      referralCode: '',
      creditScore: '',
    });
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleAddBorrower = async () => {
    if (!newBorrower.name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    try {
      setLoading(true);
      await createBorrowers({
        name: newBorrower.name.trim(),
        comments: newBorrower.comments || undefined,
        contactNumber: newBorrower.contactNumber || undefined,
        whatsappNumber: newBorrower.whatsappNumber || undefined,
        isContactSameAsWhatsapp: newBorrower.isContactSameAsWhatsapp,
        email: newBorrower.email || undefined,
        address: newBorrower.address || undefined,
        pincode: newBorrower.pincode || undefined,
        city: newBorrower.city || undefined,
        state: newBorrower.state || undefined,
        referralCode: newBorrower.referralCode || undefined,
        creditScore: newBorrower.creditScore ? parseInt(newBorrower.creditScore, 10) : undefined,
      });
      toast.success('New customer profile created');
      handleCloseAddDialog();
      fetchBorrowers();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const getCreditStatusBadge = (borrower: BorrowerAPIItem) => {
    if (borrower.blacklisted || borrower.creditStatus === 'BLOCKED') {
      return (
        <Chip
          icon={<Block sx={{ fontSize: '14px !important' }} />}
          label="Blacklisted"
          size="small"
          color="error"
          sx={{ fontWeight: 650, fontSize: '0.72rem', height: 22 }}
        />
      );
    }
    if (borrower.creditStatus === 'BAD') {
      return (
        <Chip
          icon={<Warning sx={{ fontSize: '14px !important' }} />}
          label="Watchlist"
          size="small"
          color="warning"
          sx={{ fontWeight: 650, fontSize: '0.72rem', height: 22 }}
        />
      );
    }
    return (
      <Chip
        icon={<CheckCircle sx={{ fontSize: '14px !important' }} />}
        label="Active"
        size="small"
        color="success"
        variant="outlined"
        sx={{ fontWeight: 650, fontSize: '0.72rem', height: 22 }}
      />
    );
  };

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', pb: 5 }}>
      <ToastContainer position="top-right" autoClose={2800} />

      {/* Header */}
      <PageHeader
        eyebrow="Lending · Operations"
        title="Customers"
        subtitle="Manage borrower directory, track active debt books, customer deposits, and payment statuses."
        actions={
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchBorrowers}
              disabled={loading}
              size="small"
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAddDialog}
              size="small"
              sx={{ fontWeight: 600 }}
            >
              Add Customer
            </Button>
          </Stack>
        }
      />

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Metric Strip */}
      <MetricStrip
        metrics={[
          {
            label: 'Total Customers',
            value: String(totalCustomersCount),
            hint: `${borrowersWithDebt.length} with active debt · ${zeroBalanceCount} clear`,
            tone: 'info',
          },
          {
            label: 'Book Outstanding',
            value: formatCurrency(totalOutstandingSum, 'INR', true),
            hint: `Across ${borrowersWithDebt.length} active borrowers`,
            tone: totalOutstandingSum > 0 ? 'warning' : 'default',
          },
          {
            label: 'Active Deposits',
            value: formatCurrency(totalDepositsSum, 'INR', true),
            hint: `Held in trust across ${borrowersWithDeposits.length} depositors`,
            tone: totalDepositsSum > 0 ? 'success' : 'default',
          },
          {
            label: 'Good Credit Standing',
            value: `${goodCreditCount} / ${totalCustomersCount}`,
            hint: `${blacklistedCount} blacklisted / blocked`,
            tone: blacklistedCount > 0 ? 'warning' : 'success',
          },
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
        <Tabs
          value={mainTabValue}
          onChange={(_e, val) => setMainTabValue(val)}
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44,
              py: 1,
              fontWeight: 600,
              fontSize: '0.9rem',
              textTransform: 'none',
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Borrowers Directory</span>
                <Chip
                  label={filteredCustomers.length}
                  size="small"
                  sx={{ height: 20, fontSize: '0.72rem', fontWeight: 700 }}
                />
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Global Payment Feed</span>
                {totalPayments > 0 && (
                  <Chip
                    label={totalPayments}
                    size="small"
                    sx={{ height: 20, fontSize: '0.72rem', fontWeight: 700 }}
                  />
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Tab 0: Customers Directory */}
      {mainTabValue === 0 && (
        <>
          {/* Data Toolbar: Search, Filters & Sort */}
          <DataToolbar
            end={
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Sort:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOptionType)}
                    size="small"
                    sx={{ fontSize: '0.85rem' }}
                  >
                    <MenuItem value="outstanding_desc">Outstanding: High → Low</MenuItem>
                    <MenuItem value="outstanding_asc">Outstanding: Low → High</MenuItem>
                    <MenuItem value="name_asc">Name: A → Z</MenuItem>
                    <MenuItem value="name_desc">Name: Z → A</MenuItem>
                    <MenuItem value="deposit_desc">Deposits: High → Low</MenuItem>
                    <MenuItem value="recent_payment">Most Recent Payment</MenuItem>
                    <MenuItem value="credit_score">Highest Credit Score</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            }
          >
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search by name, ID, phone, email, city…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 280, md: 320 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {/* Quick Filter Segment Chips */}
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip
                label={`All (${totalCustomersCount})`}
                size="small"
                onClick={() => setQuickFilter('all')}
                color={quickFilter === 'all' ? 'primary' : 'default'}
                variant={quickFilter === 'all' ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label={`Debt (${borrowersWithDebt.length})`}
                size="small"
                onClick={() => setQuickFilter('outstanding')}
                color={quickFilter === 'outstanding' ? 'error' : 'default'}
                variant={quickFilter === 'outstanding' ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label={`Deposits (${borrowersWithDeposits.length})`}
                size="small"
                onClick={() => setQuickFilter('deposits')}
                color={quickFilter === 'deposits' ? 'success' : 'default'}
                variant={quickFilter === 'deposits' ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label={`Zero Balance (${zeroBalanceCount})`}
                size="small"
                onClick={() => setQuickFilter('zero_balance')}
                color={quickFilter === 'zero_balance' ? 'primary' : 'default'}
                variant={quickFilter === 'zero_balance' ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600 }}
              />
              {blacklistedCount > 0 && (
                <Chip
                  label={`Blacklisted (${blacklistedCount})`}
                  size="small"
                  onClick={() => setQuickFilter('blacklisted')}
                  color={quickFilter === 'blacklisted' ? 'error' : 'default'}
                  variant={quickFilter === 'blacklisted' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Stack>
          </DataToolbar>

          {/* Section Panel for Customer Directory Table */}
          <SectionPanel
            title="Borrowers Directory"
            subtitle={`Showing ${filteredCustomers.length} matching customers (${paginatedCustomers.length} on this page)`}
          >
            {filteredCustomers.length === 0 ? (
              <EmptyState
                title="No matching customers found"
                description={
                  searchTerm
                    ? `No customers match "${searchTerm}". Try adjusting your search query or reset filter.`
                    : 'No customers available in the directory. Click "Add Customer" to register a new borrower.'
                }
                actionLabel={searchTerm ? 'Reset filters' : undefined}
                onAction={searchTerm ? () => { setSearchTerm(''); setQuickFilter('all'); } : undefined}
              />
            ) : (
              <>
                {/* Desktop Data Grid */}
                <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                        <TableCell sx={{ fontWeight: 700, py: 1.2 }}>Customer</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, py: 1.2 }}>Outstanding Debt</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, py: 1.2 }}>Active Deposits</TableCell>
                        <TableCell sx={{ fontWeight: 700, py: 1.2 }}>Last Payment</TableCell>
                        <TableCell sx={{ fontWeight: 700, py: 1.2 }}>Credit Standing</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, py: 1.2, width: 220 }}>Quick Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCustomers.map((borrower) => {
                        const avatarColor = getAvatarColor(borrower.name || 'C');
                        const debt = borrower.totalOutstanding ?? 0;
                        const dep = borrower.totalDeposit ?? 0;
                        const relPayment = formatRelativeDays(borrower.lastPaymentDate);

                        return (
                          <TableRow
                            key={borrower.id}
                            hover
                            sx={{
                              transition: 'background-color 0.15s ease',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.02),
                              },
                            }}
                          >
                            {/* Customer Profile Column */}
                            <TableCell sx={{ py: 1.2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                  sx={{
                                    bgcolor: avatarColor.bg,
                                    color: avatarColor.text,
                                    width: 36,
                                    height: 36,
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                  }}
                                >
                                  {(borrower.name || 'U').charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                      variant="body2"
                                      onClick={() => handleViewCustomer(borrower.id)}
                                      sx={{
                                        fontWeight: 700,
                                        color: 'primary.main',
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                    >
                                      {borrower.name}
                                    </Typography>
                                    <Chip
                                      label={borrower.borrowerId || `ID:${borrower.id.substring(0, 6)}`}
                                      size="small"
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.68rem',
                                        height: 18,
                                        bgcolor: 'action.hover',
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                    {borrower.contactNumber || borrower.whatsappNumber ? (
                                      <span>📞 {borrower.contactNumber || borrower.whatsappNumber}</span>
                                    ) : null}
                                    {borrower.city ? (
                                      <span> · 📍 {borrower.city}{borrower.state ? `, ${borrower.state}` : ''}</span>
                                    ) : null}
                                    {!borrower.contactNumber && !borrower.city && borrower.email ? (
                                      <span>✉️ {borrower.email}</span>
                                    ) : null}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>

                            {/* Outstanding Column */}
                            <TableCell align="right" sx={{ py: 1.2 }}>
                              {debt > 0 ? (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 700,
                                      color: 'error.main',
                                      fontSize: '0.9rem',
                                    }}
                                  >
                                    {formatCurrency(debt)}
                                  </Typography>
                                  <Typography variant="caption" color="error.dark" sx={{ fontSize: '0.68rem', fontWeight: 500 }}>
                                    Active debit
                                  </Typography>
                                </Box>
                              ) : (
                                <Chip
                                  label="₹0 · Clear"
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.72rem',
                                    height: 20,
                                    borderColor: 'divider',
                                    color: 'text.secondary',
                                  }}
                                />
                              )}
                            </TableCell>

                            {/* Deposits Column */}
                            <TableCell align="right" sx={{ py: 1.2 }}>
                              {dep > 0 ? (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 700,
                                      color: 'success.dark',
                                      fontSize: '0.9rem',
                                    }}
                                  >
                                    {formatCurrency(dep)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                    Held in trust
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  —
                                </Typography>
                              )}
                            </TableCell>

                            {/* Last Payment Column */}
                            <TableCell sx={{ py: 1.2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>
                                {borrower.lastPaymentDate
                                  ? new Date(borrower.lastPaymentDate).toLocaleDateString('en-IN', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric',
                                    })
                                  : '—'}
                              </Typography>
                              <Chip
                                label={relPayment.text}
                                size="small"
                                sx={{
                                  fontSize: '0.68rem',
                                  height: 18,
                                  bgcolor: relPayment.isRecent ? alpha(theme.palette.success.main, 0.1) : 'transparent',
                                  color: relPayment.isRecent ? 'success.dark' : 'text.secondary',
                                  fontWeight: relPayment.isRecent ? 700 : 400,
                                }}
                              />
                            </TableCell>

                            {/* Credit Standing Column */}
                            <TableCell sx={{ py: 1.2 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                {getCreditStatusBadge(borrower)}
                                {borrower.creditScore != null && (
                                  <Tooltip title={`Credit Score: ${borrower.creditScore}`}>
                                    <Chip
                                      icon={<Star sx={{ fontSize: '13px !important', color: '#D97706 !important' }} />}
                                      label={borrower.creditScore}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        bgcolor: '#FEF3C7',
                                        color: '#92400E',
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>

                            {/* Actions Column */}
                            <TableCell align="center" sx={{ py: 1.2 }}>
                              <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                <Tooltip title="View Customer Profile">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleViewCustomer(borrower.id)}
                                    sx={{ p: 0.6 }}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="View Debits & Payments">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => handleViewDebits(borrower.id)}
                                    sx={{ p: 0.6 }}
                                  >
                                    <AccountBalance fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Capture New Debit">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleOpenCaptureDebitDialog(borrower.id)}
                                    sx={{ p: 0.6 }}
                                  >
                                    <AttachMoney fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Record Deposit">
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleOpenDepositDialog(borrower)}
                                    sx={{ p: 0.6 }}
                                  >
                                    <Savings fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="More options">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleMenuOpen(e, borrower)}
                                    sx={{ p: 0.6 }}
                                  >
                                    <MoreVert fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Mobile Responsive Cards */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, p: 1.5 }}>
                  {paginatedCustomers.map((borrower) => {
                    const avatarColor = getAvatarColor(borrower.name || 'C');
                    const debt = borrower.totalOutstanding ?? 0;
                    const dep = borrower.totalDeposit ?? 0;

                    return (
                      <Card
                        key={borrower.id}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              sx={{
                                bgcolor: avatarColor.bg,
                                color: avatarColor.text,
                                width: 40,
                                height: 40,
                                fontWeight: 700,
                              }}
                            >
                              {(borrower.name || 'U').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                onClick={() => handleViewCustomer(borrower.id)}
                                sx={{ color: 'primary.main', cursor: 'pointer' }}
                              >
                                {borrower.name}
                              </Typography>
                              <Chip
                                label={borrower.borrowerId || `ID:${borrower.id.substring(0, 6)}`}
                                size="small"
                                sx={{ fontFamily: 'monospace', fontSize: '0.68rem', height: 18 }}
                              />
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {getCreditStatusBadge(borrower)}
                          </Box>
                        </Box>

                        <Grid container spacing={1} sx={{ mb: 1.5 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Outstanding</Typography>
                            <Typography variant="body1" fontWeight={700} color={debt > 0 ? 'error.main' : 'text.primary'}>
                              {formatCurrency(debt)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Active Deposits</Typography>
                            <Typography variant="body1" fontWeight={700} color="success.dark">
                              {formatCurrency(dep)}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 1 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            Last payment: {borrower.lastPaymentDate ? new Date(borrower.lastPaymentDate).toLocaleDateString('en-IN') : 'None'}
                          </Typography>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton size="small" color="primary" onClick={() => handleViewCustomer(borrower.id)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="info" onClick={() => handleViewDebits(borrower.id)}>
                              <AccountBalance fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="success" onClick={() => handleOpenCaptureDebitDialog(borrower.id)}>
                              <AttachMoney fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="secondary" onClick={() => handleOpenDepositDialog(borrower)}>
                              <Savings fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, borrower)}>
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Box>
                      </Card>
                    );
                  })}
                </Box>

                {/* Table Pagination */}
                <TablePagination
                  component="div"
                  count={filteredCustomers.length}
                  page={customerPage}
                  onPageChange={(_e, newPage) => setCustomerPage(newPage)}
                  rowsPerPage={customerRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setCustomerRowsPerPage(parseInt(e.target.value, 10));
                    setCustomerPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                />
              </>
            )}
          </SectionPanel>
        </>
      )}

      {/* Tab 1: Global Payment Feed */}
      {mainTabValue === 1 && (
        <SectionPanel
          title="Global Payment Feed"
          subtitle={`All recorded payments across the portfolio (${totalPayments} total)`}
          actions={
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={fetchAllPayments}
              disabled={loadingAllPayments}
            >
              Refresh
            </Button>
          }
        >
          {loadingAllPayments && <LinearProgress sx={{ mb: 2 }} />}

          {/* Payment search and filter bar */}
          <DataToolbar>
            <TextField
              size="small"
              placeholder="Search payments by borrower name, note, debit ID…"
              value={paymentSearch}
              onChange={(e) => setPaymentSearch(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 300 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: paymentSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setPaymentSearch('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={paymentTypeFilter}
                label="Payment Type"
                onChange={(e) => {
                  setPaymentTypeFilter(e.target.value);
                  setPaymentPage(0);
                }}
              >
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="INTEREST">Interest</MenuItem>
                <MenuItem value="REPAYMENT">Repayment (Principal)</MenuItem>
                <MenuItem value="LATE_FEE">Late Fee</MenuItem>
                <MenuItem value="TOP_UP">Top Up</MenuItem>
              </Select>
            </FormControl>
          </DataToolbar>

          {filteredPayments.length === 0 && !loadingAllPayments ? (
            <EmptyState
              title="No payment records found"
              description="No payments matching your query. Record payments against active debit accounts."
            />
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                      <TableCell sx={{ fontWeight: 700 }}>Payment ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Customer / Borrower</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Received By</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments.map((payment) => (
                      <TableRow key={payment.id || payment.paymentId} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {(payment.id || payment.paymentId || '').substring(0, 8)}…
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                            {payment.createdDate
                              ? new Date(payment.createdDate).toLocaleDateString('en-IN', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })
                              : payment.createdAt
                              ? new Date(payment.createdAt).toLocaleDateString('en-IN')
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: 'success.dark',
                              fontSize: '0.88rem',
                            }}
                          >
                            {formatCurrency(payment.amount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.paymentType || 'N/A'}
                            size="small"
                            color={
                              payment.paymentType === 'REPAYMENT'
                                ? 'success'
                                : payment.paymentType === 'INTEREST'
                                ? 'warning'
                                : payment.paymentType === 'LATE_FEE'
                                ? 'error'
                                : 'default'
                            }
                            sx={{ fontWeight: 650, fontSize: '0.72rem', height: 20 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="primary.main"
                            onClick={() => payment.borrowerId && handleViewCustomer(payment.borrowerId)}
                            sx={{ cursor: payment.borrowerId ? 'pointer' : 'default', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {payment.borrowerName || payment.customerName || payment.borrowerId || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                            {payment.paymentReceivedBy || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 220, display: 'block' }}>
                            {payment.note || '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalPayments}
                page={paymentPage}
                onPageChange={(_e, p) => setPaymentPage(p)}
                rowsPerPage={paymentRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setPaymentRowsPerPage(parseInt(e.target.value, 10));
                  setPaymentPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
              />
            </>
          )}
        </SectionPanel>
      )}

      {/* More Options Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            borderRadius: 2,
            minWidth: 200,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (selectedBorrowerForMenu) handleViewCustomer(selectedBorrowerForMenu.id);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Visibility fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="View Customer Profile" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedBorrowerForMenu) handleViewDebits(selectedBorrowerForMenu.id);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <AccountBalance fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText primary="Debits & Timeline" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedBorrowerForMenu) navigate(`/admin/customers/${selectedBorrowerForMenu.id}/interest-report`);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Assessment fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText primary="Interest Audit Report" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedBorrowerForMenu) navigate(`/admin/customers/${selectedBorrowerForMenu.id}/edit`);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit Profile" />
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => {
            if (selectedBorrowerForMenu) handleOpenDepositDialog(selectedBorrowerForMenu);
          }}
        >
          <ListItemIcon>
            <Savings fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText primary="Record Deposit" />
        </MenuItem>
        <MenuItem onClick={handleNotifyCustomer}>
          <ListItemIcon>
            <Notifications fontSize="small" color="action" />
          </ListItemIcon>
          <ListItemText primary="Send Balance Notice" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedBorrowerForBrochure(selectedBorrowerForMenu);
            setOpenBrochureDialog(true);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <MenuBook fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Welcome Brochure" />
        </MenuItem>
      </Menu>

      {/* Add New Customer Modal */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontFamily: '"Fraunces", serif' }}>
          Add New Borrower / Customer
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Basic Identification
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Full Name *"
                  placeholder="e.g. Ramesh Kumar"
                  value={newBorrower.name}
                  onChange={(e) => setNewBorrower({ ...newBorrower, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  placeholder="name@example.com"
                  value={newBorrower.email}
                  onChange={(e) => setNewBorrower({ ...newBorrower, email: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notes / Comments"
                  placeholder="Borrower background, reference notes, business context..."
                  value={newBorrower.comments}
                  onChange={(e) => setNewBorrower({ ...newBorrower, comments: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>

            <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>
              Contact Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Contact Phone Number"
                  placeholder="10-digit mobile number"
                  value={newBorrower.contactNumber}
                  onChange={(e) => {
                    const v = e.target.value;
                    setNewBorrower({
                      ...newBorrower,
                      contactNumber: v,
                      whatsappNumber: newBorrower.isContactSameAsWhatsapp ? v : newBorrower.whatsappNumber,
                    });
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="WhatsApp Number"
                  placeholder="WhatsApp mobile number"
                  value={newBorrower.whatsappNumber}
                  onChange={(e) => setNewBorrower({ ...newBorrower, whatsappNumber: e.target.value })}
                  disabled={newBorrower.isContactSameAsWhatsapp}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WhatsApp fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newBorrower.isContactSameAsWhatsapp}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNewBorrower({
                          ...newBorrower,
                          isContactSameAsWhatsapp: checked,
                          whatsappNumber: checked ? newBorrower.contactNumber : newBorrower.whatsappNumber,
                        });
                      }}
                    />
                  }
                  label="Contact number is same as WhatsApp number"
                />
              </Grid>
            </Grid>

            <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>
              Address & Location
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Street Address"
                  placeholder="House/Plot, Building, Street, Area"
                  value={newBorrower.address}
                  onChange={(e) => setNewBorrower({ ...newBorrower, address: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  value={newBorrower.city}
                  onChange={(e) => setNewBorrower({ ...newBorrower, city: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="State"
                  value={newBorrower.state}
                  onChange={(e) => setNewBorrower({ ...newBorrower, state: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Pincode"
                  value={newBorrower.pincode}
                  onChange={(e) => setNewBorrower({ ...newBorrower, pincode: e.target.value })}
                />
              </Grid>
            </Grid>

            <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>
              CRM & Credit Scoring
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Referral Code"
                  placeholder="e.g. REF-104"
                  value={newBorrower.referralCode}
                  onChange={(e) => setNewBorrower({ ...newBorrower, referralCode: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Credit Score (0-900)"
                  type="number"
                  placeholder="e.g. 750"
                  value={newBorrower.creditScore}
                  onChange={(e) => setNewBorrower({ ...newBorrower, creditScore: e.target.value })}
                  inputProps={{ min: 0, max: 900 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseAddDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddBorrower}
            disabled={loading || !newBorrower.name.trim()}
            sx={{ fontWeight: 600 }}
          >
            {loading ? 'Creating…' : 'Save Customer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Deposit Modal */}
      <Dialog open={openDepositDialog} onClose={handleCloseDepositDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: '"Fraunces", serif' }}>
          Record Customer Deposit
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Recording deposit for <strong>{depositBorrower?.name}</strong>. An immutable audit trail is saved automatically.
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Deposit Amount (INR) *"
            type="number"
            value={depositForm.amount}
            onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })}
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            fullWidth
            size="small"
            label="Deposit Date *"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={depositForm.depositDate}
            onChange={(e) => setDepositForm({ ...depositForm, depositDate: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Maturity Date (optional)"
            type="date"
            InputLabelProps={{ shrink: true }}
            helperText="System sends maturity reminders when due"
            value={depositForm.maturityDate}
            onChange={(e) => setDepositForm({ ...depositForm, maturityDate: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Payment Source</InputLabel>
            <Select
              value={depositForm.source}
              label="Payment Source"
              onChange={(e) => setDepositForm({ ...depositForm, source: e.target.value })}
            >
              <MenuItem value="BANK_TRANSFER">Bank Transfer (NEFT / IMPS / RTGS)</MenuItem>
              <MenuItem value="UPI">UPI Payment</MenuItem>
              <MenuItem value="CASH">Cash Deposit</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Reference / Transaction ID"
            placeholder="e.g. UTR104928104"
            value={depositForm.referenceNumber}
            onChange={(e) => setDepositForm({ ...depositForm, referenceNumber: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Notes"
            placeholder="Deposit conditions or customer instructions"
            multiline
            rows={2}
            value={depositForm.notes}
            onChange={(e) => setDepositForm({ ...depositForm, notes: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={depositForm.notifyCustomer}
                onChange={(e) => setDepositForm({ ...depositForm, notifyCustomer: e.target.checked })}
              />
            }
            label="Send deposit confirmation email to customer profile"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDepositDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmitDeposit}
            disabled={savingDeposit || !depositForm.amount}
            sx={{ fontWeight: 600 }}
          >
            {savingDeposit ? 'Recording…' : 'Confirm Deposit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notify Customer Modal */}
      <Dialog open={openNotifyDialog} onClose={handleCloseNotifyDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: '"Fraunces", serif' }}>
          Send Balance Notification
        </DialogTitle>
        <DialogContent dividers>
          {selectedBorrowerForMenu && (
            <Box sx={{ mb: 2.5, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {selectedBorrowerForMenu.name}
              </Typography>
              <Typography variant="body2" color="error.main" fontWeight={700}>
                Total Outstanding: {formatCurrency(selectedBorrowerForMenu.totalOutstanding || 0)}
              </Typography>
            </Box>
          )}

          <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              Recipient Contact
            </FormLabel>
            <RadioGroup
              value={notificationData.useSystemContact ? 'system' : 'manual'}
              onChange={(e) =>
                setNotificationData({
                  ...notificationData,
                  useSystemContact: e.target.value === 'system',
                  email:
                    e.target.value === 'system' && selectedBorrowerForMenu
                      ? selectedBorrowerForMenu.email || ''
                      : notificationData.email,
                  contactNumber:
                    e.target.value === 'system' && selectedBorrowerForMenu
                      ? selectedBorrowerForMenu.contactNumber || ''
                      : notificationData.contactNumber,
                })
              }
            >
              <FormControlLabel
                value="system"
                control={<Radio size="small" />}
                label={`Use profile contact (${selectedBorrowerForMenu?.email || 'No email'} / ${selectedBorrowerForMenu?.contactNumber || 'No phone'})`}
              />
              <FormControlLabel value="manual" control={<Radio size="small" />} label="Enter custom destination" />
            </RadioGroup>
          </FormControl>

          {!notificationData.useSystemContact && (
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Email Address"
                type="email"
                value={notificationData.email}
                onChange={(e) => setNotificationData({ ...notificationData, email: e.target.value })}
              />
              <TextField
                fullWidth
                size="small"
                label="Mobile Number"
                value={notificationData.contactNumber}
                onChange={(e) => setNotificationData({ ...notificationData, contactNumber: e.target.value })}
              />
            </Stack>
          )}

          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              Channels
            </FormLabel>
            <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationData.sendEmail}
                    onChange={(e) => setNotificationData({ ...notificationData, sendEmail: e.target.checked })}
                  />
                }
                label="Send Email"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationData.sendSMS}
                    onChange={(e) => setNotificationData({ ...notificationData, sendSMS: e.target.checked })}
                  />
                }
                label="Send SMS"
              />
            </Stack>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseNotifyDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendNotification}
            disabled={loading || (!notificationData.sendEmail && !notificationData.sendSMS)}
            sx={{ fontWeight: 600 }}
          >
            {loading ? 'Sending…' : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Capture Debit Modal */}
      {selectedBorrowerId && (
        <DebitCaptureModal
          open={openCaptureDebitDialog}
          borrowerId={selectedBorrowerId}
          borrower={borrowers.find((b) => b.id === selectedBorrowerId) || undefined}
          handleDebitModelClose={handleCloseCaptureDebitDialog}
          onDebitCreated={handleDebitCreated}
        />
      )}

      {/* Welcome Brochure Viewer */}
      <BrochureViewer
        open={openBrochureDialog}
        onClose={() => {
          setOpenBrochureDialog(false);
          setSelectedBorrowerForBrochure(null);
        }}
        initialBorrowerId={selectedBorrowerForBrochure?.id}
      />
    </Box>
  );
};

export default CustomerManagement;
